import { useState } from 'react';
import { css } from '@emotion/react';
import styled from '@emotion/styled';
import { currencyFormatter, percentageFormatter } from '@dnhb/formatters';
import {useDispatch, useSelector} from 'shared/hooks';
import { LabelText, ResultDetail, Heading, Display, Info, Collapsable, Currency,Loader } from 'shared/components';
import { ConversieButtons } from 'shared/conversie';
import { ResultCard } from 'shared/containers';
import { CollapseArrowSvg, NoSavingSvg } from 'images/shared';
import {whiteListUrlParams} from '../config'
import { getParamsFromState } from 'shared/helpers';
import { Checkbox } from 'shared/inputs';
import { dispatchWithRequest, SYNC, sync } from 'shared/wizard/actions';
import Color from "color";

const Wrapper = styled.div`
  > div:not(:last-of-type) {
    margin-bottom: 24px;
  }
  @media ${({ theme }) => theme.tablet} {
    > div:not(:last-of-type) {
      margin-bottom: 16px;
    }
  }
`;

const DetailedCollapsable = styled(Collapsable)`
  margin-bottom: 0;
  > button {
    padding: 24px;
  }

  > div {
    padding-top: 0;
    padding-bottom: 24px;
    > div:first-of-type {
      border-top: 1px solid ${({ theme }) => theme.borderColor};
      padding-top: 16px;
    }
    > div:last-of-type {
      border-bottom: 1px dashed ${({ theme }) => theme.borderColor};
      padding-bottom: 16px;
    }
  }
`;

const SubCollapsableButton = styled.button(
  ({ theme, isOpen }) => css`
    font-family: ${theme.fontFamily};
    color: ${theme.highlightColor};
    font-size: 14px;
    line-height: 18px;
    font-weight: 600;
    letter-spacing: -0.5px;
    cursor: pointer;

    display: inline-flex;
    justify-content: space-between;
    align-items: center;
    margin-left: 24px;

    @media ${theme.mobile} {
      display: flex;
      margin: 0;
      font-size: 12px;
    }

    svg {
      display: block;
      margin-left: 8px;
      height: 8px;
      width: 12px;
      fill: ${theme.highlightColor};
      transform: ${isOpen ? 'rotate(180deg)' : 'rotate(0deg)'};
      transition: transform 200ms;
    }
  `
);

const CalculationBlock = styled.div`
  border-bottom: 1px solid ${({ theme }) => theme.borderColor};
  padding: 16px 0;
  > div {
    padding: 16px 0;
  }
`;

const NoResultIcon = styled(NoSavingSvg)`
  margin-bottom: 24px;
  height: 56px;
  width: 56px;
  display: block;
  fill: ${({ theme }) => theme.highlightColor};
`;

const LoaderContainer = styled.section`
  display: flex;
  border-radius: inherit;
  align-items: center;
  justify-content: center;
  height: 100%;
  width: 100%;
  background-color: ${({ theme }) =>
  Color(theme.cardColor)
    .alpha(0.7)
    .rgb()
    .string()};
  position: absolute;
  top: 0;
  bottom: 0;
  left: 0;
  right: 0;
`;

const SubCollapsable = ({ title, value, children }) => {
  const [isOpen, setIsOpen] = useState(false);

  return (
    <CalculationBlock>
      <ResultDetail
        valuePrefix="+"
        fontWeight="600"
        value={value}
        css={{ cursor: 'pointer', marginBottom: isOpen ? '16px' : 0 }}
        onClick={() => setIsOpen(open => !open)}
      >
        <div>
          {title}
          <SubCollapsableButton isOpen={isOpen} type="button">
            {isOpen ? 'Kosten verbergen' : 'Kosten tonen'}
            <CollapseArrowSvg />
          </SubCollapsableButton>
        </div>
      </ResultDetail>
      {isOpen && children}
    </CalculationBlock>
  );
};

const OutputFinal = () => {
  const {
    app,
    meta,
    settings,
    input,
    output: {
      data: {
        first_monthly_payment,
        additional_costs,
        required_mortgage,
        appraisal_fee,
        nhg_costs,
        bank_guarantee,
        transfer_tax,
        notary_costs,
        consultancy_costs,
        possibility = {},
        energy_saving_extra_max_mortgage_amount
      },
      success
    },
    syncing
  } = useSelector(state => state);

  const dispatch = useDispatch();

  const positiveResult = possibility.extra === 0;
  const extraResourceText = `${input.has_own_resources ? 'Extra b' : 'B'}enodigd eigen geld`;
  const nextYear = String(new Date().getFullYear() + 1);
  const {newRegulationsSwitch} = settings;

  const onChangeHandler = (prop, value) => {
    dispatch({ type: SYNC, syncing: true });
    dispatch(
      dispatchWithRequest(prop, value, '/kidwk/calculate/extras', '', () =>
        dispatch({ type: SYNC, syncing: false })
      )
    );
  };


  return success ? (
    <Wrapper>
      {positiveResult ? (
        <Heading>
          <strong>Gefeliciteerd!</strong> Het lijkt erop dat je de woning kunt kopen
        </Heading>
      ) : (
        <Heading>
          Let op. Je hebt{' '}
          <Currency value={possibility.extra} fontSize="30" fontWeight="800" disableFontSizeLimitOfPrefix /> extra eigen
          geld nodig voor deze woning
        </Heading>
      )}
      <ResultCard css={{position: 'relative'}}>
        {syncing && (
          <LoaderContainer>
            <Loader />
          </LoaderContainer>
        )}
        <ResultDetail value={required_mortgage}>Nodig voor de aankoop van de woning</ResultDetail>
        <ResultDetail value={first_monthly_payment}>
          Eerste maandlasten
        </ResultDetail>
        <ResultDetail value={possibility.maximum_mortgage_by_income}>
          <div>
            Maximaal te lenen met je inkomen
            <Info title="Maximaal te lenen met je inkomen">
              <p>
                Er is verschil tussen wat je kunt lenen op basis van alleen je inkomen of op basis van de waarde van de
                woning (na een eventuele verbouwing). Hoewel je o.b.v. je inkomen misschien meer kunt lenen, blijft een
                financiering altijd beperkt tot maximaal 100% van de waarde van de woning. Voor extra uitgaven aan
                energiebesparende maatregelen geldt een uitzondering. Informeer hiernaar bij je adviseur.
              </p>
              {energy_saving_extra_max_mortgage_amount > 0 && (
                <p>
                  Op basis van het opgegeven energielabel kun je nog tot{' '}
                  {currencyFormatter(energy_saving_extra_max_mortgage_amount)}
                  {' '}extra lenen. Informeer hiernaar bij je adviseur.
                </p>
              )}
              <p>
                De maximale hypotheek op basis van je inkomen volgt de zogenaamde &apos;Loan-to-income&apos; index. Deze
                index bepaalt o.b.v. je inkomen én je huidige financiële verplichtingen wat je per maand maximaal aan je
                hypotheek mag besteden. Let op, overleg goed met je adviseur of het verstandig is het maximale bedrag
                aan hypotheek ook echt te benutten.
              </p>
            </Info>
          </div>
        </ResultDetail>
        {!positiveResult && <ResultDetail value={possibility.extra}>{extraResourceText}</ResultDetail>}
      </ResultCard>
      <ConversieButtons settings={settings} app={app}
                        urlLeadFormParams={getParamsFromState(input, whiteListUrlParams)} />
      <DetailedCollapsable title="Wat hebben we voor je berekend?">
        <div css={{position: 'relative'}}>
          {syncing && (
            <LoaderContainer>
              <Loader />
            </LoaderContainer>
          )}
          <ResultDetail fontWeight="600" value={input.purchase_price}>
            Koopsom
          </ResultDetail>
          <SubCollapsable title="Bijkomende kosten" value={additional_costs}>
            <Display
              label="Overdrachts&shy;belasting"
              value={currencyFormatter(transfer_tax)}
              infoText={`
    Kopers van 18 tot 35 jaar betalen eenmalig geen overdrachtsbelasting bij de aankoop van een woning. 
    De leeftijd op de datum van de overdracht is hiervoor bepalend. 
    Vanaf 1 april 2021 geldt aanvullend dat de woning niet duurder mag zijn dan € 400.000. 
    De eenmalige vrijstelling geldt alleen als de kopers de woning zelf gaan bewonen. 
    Voor kopers vanaf 35 jaar geldt het tarief van ${percentageFormatter(meta.transfer_tax_percentage, 0)}. 
            `}
            />
            {Boolean(nhg_costs) && (
              <Display
                label="Indicatie NHG kosten"
                value={currencyFormatter(nhg_costs)}
                infoText={`
		Sluit je je hypotheek af met een Nationale Hypotheek Garantie (NHG) dan kost je dit een percentage van het hypotheekbedrag. 
		Je betaalt deze zogenaamde 'borgtochtprovisie' bij het afsluiten van je hypotheek aan de geldverstrekker. 
		Net als je hypotheek en overige kosten regel je dit via de notaris.
              `}
              />
            )}
            <Display
              label="Notariskosten"
              value={currencyFormatter(notary_costs)}
              infoText={`
        	Om je hypotheek definitief af te sluiten moet je eerst langs een notaris. 
		Welke bepaal je zelf. De notaris zorgt ervoor dat je huis en je hypotheek worden ingeschreven in een openbaar register: het Kadaster. 
		Je betaalt dus altijd notariskosten bij een nieuwe hypotheek.
              `}
            />
            <Display
              label="Advieskosten"
              value={currencyFormatter(consultancy_costs)}
              infoText={`
		Je hypotheekadviseur bespreekt je situatie en (financiële) plannen met je en bekijkt welke hypotheek daar het beste bij past. 
		Je krijgt ook advies over welke verzekeringen daarop goed aansluiten. Je betaalt de adviseur een vast bedrag.
              `}
            />
            <Display
              label="Taxatiekosten"
              value={currencyFormatter(appraisal_fee)}
              infoText={`
		Voordat je een hypotheeklening krijgt, wil de geldverstrekker weten wat je (nieuwe) woning waard is. 
		Dat kan alleen via een 'gevalideerde taxatie'. 
		Hiervoor  onderzoekt een onafhankelijke taxateur de woning en legt hij vast in welke staat deze verkeert en wat de actuele marktwaarde is. 
		De kosten van zo'n taxatierapport verschillen per taxateur of makelaar. Je kiest zelf wie jouw taxatie regelt. 
              `}
            />
            <Display
              label="Bankgarantie"
              value={currencyFormatter(bank_guarantee)}
              infoText={`
		Soms verlangt de verkoper een bankgarantie van je; hij is er dan zeker van, dat je als koper de borgsom betaalt, 
		indien de koopovereenkomst wordt ontbonden
              `}
            />
          </SubCollapsable>
          {(input.has_own_resources || input.has_surplus_value) && (
            <CalculationBlock>
              <ResultDetail value={input.purchase_price + additional_costs}>
                <strong>Totale kosten</strong>
              </ResultDetail>
              {input.has_surplus_value && (
                <ResultDetail valuePrefix="-" fontWeight="600" value={input.surplus_value}>
                  Inbreng overwaarde oude woning
                </ResultDetail>
              )}
              {input.has_own_resources && (
                <ResultDetail valuePrefix="-" fontWeight="600" value={input.own_resources}>
                  Inbreng eigen (spaar)geld
                </ResultDetail>
              )}
            </CalculationBlock>
          )}
          {positiveResult ? (
            <>
              <CalculationBlock>
                <ResultDetail value={required_mortgage}>
                  <strong>Benodigde hypotheek</strong>
                </ResultDetail>
              </CalculationBlock>
              <CalculationBlock>
                <ResultDetail fontWeight="800" value={possibility.maximum_mortgage}>
                  <div>
                    <strong>Maximaal te lenen met je inkomen</strong>
                    <Info title="Maximaal te lenen met je inkomen">
                      <p>
                        Er is verschil tussen wat je kunt lenen op basis van alleen je inkomen of op basis van de waarde
                        van de woning (na een eventuele verbouwing). Hoewel je o.b.v. je inkomen misschien meer kunt
                        lenen, blijft een financiering altijd beperkt tot maximaal 100% van de waarde van de woning.
                        Voor
                        extra uitgaven aan energiebesparende maatregelen geldt een uitzondering. Informeer hiernaar bij
                        je
                        adviseur.
                      </p>
                      <p>
                        De maximale hypotheek op basis van je inkomen volgt de zogenaamde &apos;Loan-to-income&apos;
                        index. Deze index bepaalt o.b.v. je inkomen én je huidige financiële verplichtingen wat je per
                        maand maximaal aan je hypotheek mag besteden. Let op, overleg goed met je adviseur of het
                        verstandig is het maximale bedrag aan hypotheek ook echt te benutten.
                      </p>
                    </Info>
                  </div>
                </ResultDetail>
              </CalculationBlock>
            </>
          ) : (
            <>
              <ResultDetail value={required_mortgage}>
                <strong>Benodigde hypotheeksom</strong>
              </ResultDetail>
              <CalculationBlock>
                <ResultDetail valuePrefix="-" fontWeight="600" value={possibility.maximum_mortgage}>
                  <div>
                    Maximaal te lenen
                    <Info title="Maximaal te lenen">
                      <p>
                        Er is verschil tussen wat je kunt lenen op basis van alleen je inkomen of op basis van de waarde
                        van de woning (na een eventuele verbouwing). Hoewel je o.b.v. je inkomen misschien meer kunt
                        lenen, blijft een financiering altijd beperkt tot maximaal 100% van de waarde van de woning.
                        Voor
                        extra uitgaven aan energiebesparende maatregelen geldt een uitzondering. Informeer hiernaar bij
                        je
                        adviseur.
                      </p>
                      <p>
                        De maximale hypotheek op basis van je inkomen volgt de zogenaamde &apos;Loan-to-income&apos;
                        index. Deze index bepaalt o.b.v. je inkomen én je huidige financiële verplichtingen wat je per
                        maand maximaal aan je hypotheek mag besteden. Let op, overleg goed met je adviseur of het
                        verstandig is het maximale bedrag aan hypotheek ook echt te benutten.
                      </p>
                    </Info>
                  </div>
                </ResultDetail>
              </CalculationBlock>
              <ResultDetail value={possibility.extra}>
                <strong>{extraResourceText}</strong>
              </ResultDetail>
            </>
          )}
        </div>
      </DetailedCollapsable>

      {newRegulationsSwitch ? <Collapsable title="Waar wil je rekening mee houden?" className="extra-options">
        <Checkbox
          name="new_regulations"
          value={input.new_regulations}
          onChange={e => {
            sync(dispatch)('new_regulations', e.target.value)
            // also change energy label to next year regulations
            if (e.target.value && ['ENERGY_NEUTRAL', 'ZERO-ON-THE-METER'].includes(input.energy_label)) {
              onChangeHandler('energy_label', 'A++++_WITH_ENERGY_PERFORMANCE_GUARANTEE');
            } else if (input.energy_label === 'A++++_WITH_ENERGY_PERFORMANCE_GUARANTEE') {
              onChangeHandler('energy_label', 'ZERO-ON-THE-METER');
            } else if (input.energy_label === 'A+++++') {
              onChangeHandler('energy_label', 'A++++');
            } else {
              // trigger submit
              onChangeHandler('new_regulations', e.target.value);
            }
          }}
          label={`Toon resultaten op basis van regelgeving in ${nextYear}`}
        />
      </Collapsable> : null}

    </Wrapper>
  ) : (
    <>
      <NoResultIcon />
      <Heading>Sorry. Wij hebben geen resultaat voor jou kunnen vinden.</Heading>
      <LabelText css={{marginBottom: '24px'}}>
        Wij raden je aan om een afspraak te maken met jouw adviseur om tot een oplossing te komen voor jouw situatie.
      </LabelText>
      <ConversieButtons settings={settings} app={app} urlLeadFormParams={getParamsFromState(input, whiteListUrlParams)}
                        hidePdfButton />
    </>
  );
};

export default OutputFinal;
